import { useQuery } from '@apollo/client'
import { round } from 'lodash'

import {
  CropFilterContext,
  HistoricalSeasonFilterContext,
  RegionFilterContext,
  VarietyFilterContext,
} from 'contexts'
import { HarvestFiltersContext } from 'features/MainRouter/contexts'
import { MiscUtils } from 'utils'
import { AnalyticsTimeMode, RiceLotStatus } from 'types'

import {
  CombinedHarvestStats,
  CombinedHarvestStatsVariables,
  GET_COMBINED_HARVEST_STATS,
} from './api'

const MIN_BAR_VALUE = 5

export const useOrganizationCompaniesHarvestStats = () => {
  const { region } = RegionFilterContext.useRegionFilterContext()
  const { selectedCrop } = CropFilterContext.useCropFilterContext()
  const { selectedVarietyId } = VarietyFilterContext.useVarietyFilterContext()
  const { selectedHistoricalSeason } =
    HistoricalSeasonFilterContext.useHistoricalSeasonFilterContext()
  const { company, subregion } = HarvestFiltersContext.useHarvestFiltersContext()

  const cropId = selectedCrop?.id

  const { data, loading } = useQuery<CombinedHarvestStats, CombinedHarvestStatsVariables>(
    GET_COMBINED_HARVEST_STATS,
    {
      variables: {
        regionId: region?.id,
        subregionId: subregion?.id,
        companyId: company?.id,
        cropId: cropId!,
        varietyId: MiscUtils.formatVarietyId(selectedVarietyId),
        seasonStartRange: selectedHistoricalSeason?.startRange,
        seasonEndRange: selectedHistoricalSeason?.endRange,
        timeMode: AnalyticsTimeMode.NEXT_TWO_WEEKS,
        status: [RiceLotStatus.HARVEST],
      },
      skip: !cropId,
    },
  )

  const getPercentage = (value: number, total: number) => {
    if (!value) return 0

    return round((value / total) * 100)
  }

  const getBarValue = (percentage: number, harvested: number) => {
    return !!harvested && percentage < MIN_BAR_VALUE ? MIN_BAR_VALUE : percentage
  }

  const lotsChartData =
    data?.currentHarvestStats.companies.map(
      ({ company: organizationCompany, lotsStats, totals }) => {
        const nextTwoWeeksCompany = data?.nextTwoWeeksHarvestStats.companiesSummary?.find(
          item => item.company.id === organizationCompany.id,
        )
        const currentPercentage = getPercentage(lotsStats.harvestDate.lots, totals.lots)
        const nextTwoWeeksHarvested =
          (nextTwoWeeksCompany?.totals.lots ?? 0) - lotsStats.harvestDate.lots

        const nextTwoWeeksPercentage = getPercentage(nextTwoWeeksHarvested, totals.lots)
        return {
          name: organizationCompany.name,
          values: {
            current: {
              total: totals.lots,
              harvested: lotsStats.harvestDate.lots,
              percentage: currentPercentage,
              barValue: getBarValue(currentPercentage, lotsStats.harvestDate.lots),
            },
            nextTwoWeeks: {
              total: totals.lots,
              harvested: nextTwoWeeksHarvested,
              percentage: nextTwoWeeksPercentage,
              barValue: getBarValue(nextTwoWeeksPercentage, nextTwoWeeksHarvested),
            },
          },
        }
      },
    ) ?? []

  const sizeChartData =
    data?.currentHarvestStats.companies.map(
      ({ company: organizationCompany, lotsStats, totals }) => {
        const nextTwoWeeksCompany = data?.nextTwoWeeksHarvestStats.companiesSummary?.find(
          item => item.company.id === organizationCompany.id,
        )

        const currentPercentage = getPercentage(lotsStats.harvestDate.size, totals.size)
        const nextTwoWeeksHarvested =
          (nextTwoWeeksCompany?.totals.size ?? 0) - lotsStats.harvestDate.size

        const nextTwoWeeksPercentage = getPercentage(nextTwoWeeksHarvested, totals.size)

        return {
          name: organizationCompany.name,
          values: {
            current: {
              total: round(totals.size),
              harvested: round(lotsStats.harvestDate.size),
              percentage: currentPercentage,
              barValue: getBarValue(currentPercentage, lotsStats.harvestDate.size),
            },
            nextTwoWeeks: {
              total: round(totals.size),
              harvested: round(nextTwoWeeksHarvested),
              percentage: nextTwoWeeksPercentage,
              barValue: getBarValue(nextTwoWeeksPercentage, nextTwoWeeksHarvested),
            },
          },
        }
      },
    ) ?? []

  return { lotsChartData, sizeChartData, loading }
}
